import { useCallback, useState } from 'react';

/**
 * Copy `chrome://extensions/shortcuts` to clipboard with visible feedback.
 *
 * Why a button (not an anchor): chrome:// URLs cannot be opened from page
 * JS — Chrome refuses the navigation. The only workable user path is
 * paste-into-URL-bar, so we copy and trust the user to paste.
 *
 * This is the always-available fallback for surfaces that ALSO offer the
 * extension-bridged "open chrome://extensions/shortcuts" action — if that
 * action fails (extension not installed, old version, policy block), this
 * button still works.
 *
 * Variants:
 *   - `default` (amber): used in the onboarding wizard, where the user
 *     has hit a failure path and we want the affordance to be noticeable.
 *   - `quiet` (neutral): used in /settings#shortcuts, where the link
 *     sits alongside other CTAs and shouldn't shout for attention.
 */
export function CopyShortcutsLink({ variant = 'default' }: { variant?: 'default' | 'quiet' }) {
  const URL_STR = 'chrome://extensions/shortcuts';
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const onClick = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(URL_STR);
      setState('copied');
      window.setTimeout(() => setState('idle'), 2200);
    } catch {
      setState('failed');
      window.setTimeout(() => setState('idle'), 2200);
    }
  }, []);

  const visualClass =
    variant === 'quiet'
      ? 'inline-flex items-center gap-1.5 rounded-sm border border-neutral-200 bg-white px-2 py-1 font-mono text-[11px] text-neutral-700 hover:bg-neutral-50'
      : 'inline-flex items-center gap-1.5 rounded-sm border border-amber-300 bg-amber-100/60 px-2 py-1 font-mono text-[11px] text-amber-900 hover:bg-amber-100';

  const tailLabelClass =
    variant === 'quiet'
      ? 'text-[10px] font-sans text-neutral-500'
      : 'text-[10px] font-sans text-amber-700';

  return (
    <button type="button" onClick={onClick} className={visualClass}>
      <span>{URL_STR}</span>
      <span className={tailLabelClass}>
        {state === 'copied'
          ? '· Copied'
          : state === 'failed'
            ? "· Couldn't copy — select and copy manually"
            : '· Click to copy'}
      </span>
    </button>
  );
}
